import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import api from '../config/api';
import PartnerPlanWall from '../components/PartnerPlanWall';
import StripeCheckout from '../components/StripeCheckout';
import { usePayment } from '../contexts/PaymentContext';
import PartnerWorkspaceFooter from '../components/PartnerWorkspaceFooter';
import { DOCUMENT } from '../constants/documentTheme';
import {
  partnerLinkSx,
  partnerOutlineBtnSx,
  partnerOxbloodBtnSx,
  partnerPageInnerSx,
  partnerPageWrapSx
} from '../components/PartnerListChrome';
import { BillingIntervalToggle } from '../components/PricingChrome';
import PartnerOnboardingCard, { PartnerDeveloperApiCard } from '../components/PartnerAddonCard';
import {
  PARTNER_PLANS,
  PARTNER_ONBOARDING_TIER_TYPE,
  PARTNER_DEVELOPER_API_TIER_TYPE,
  partnerPlanFeatures,
  partnerSalesMailto
} from '../config/plans';
import {
  billingIntervalFromYearly,
  formatPlanLimit,
  isCurrentSelfServePlan,
  partnerCheckoutTierType,
  partnerPlanCtaLabel,
  partnerPlanTripsSummary,
  planNameKey,
  isScheduledSelfServePlan,
  formatOverLimitSummary
} from '../utils/partnerPlan';

const fieldDtSx = {
  fontSize: 10,
  letterSpacing: '0.19em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  mb: 0.75,
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = parseISO(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const PartnerBilling = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createCheckoutSession, loading: checkoutLoading, error: checkoutError, clearError } = usePayment();
  const [entitlements, setEntitlements] = useState(null);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [commissionInvoices, setCommissionInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wallOpen, setWallOpen] = useState(false);
  const [yearly, setYearly] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [scheduleNotice, setScheduleNotice] = useState('');
  const [cancelScheduleLoading, setCancelScheduleLoading] = useState(false);

  const openBillingPortal = async () => {
    try {
      setPortalLoading(true);
      setError('');
      const response = await api.post('/payments/billing-portal', {
        returnUrl: window.location.href
      });
      if (response.data?.url) {
        window.location.assign(response.data.url);
        return;
      }
      setError(t('partnerPlans.portalFailed'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerPlans.portalFailed'));
    } finally {
      setPortalLoading(false);
    }
  };

  const fetchBilling = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('partnerToken');
      if (!token) {
        navigate('/partner/login');
        return;
      }
      const response = await api.get('/partners/billing');
      setEntitlements(response.data.entitlements);
      setYearly(response.data.entitlements?.billingInterval === 'year');
      setStripeAccount(response.data.stripe || null);
      setPaymentHistory(response.data.paymentHistory || []);
      setCommissionInvoices(response.data.commissionInvoices || []);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/partner/login');
        return;
      }
      setError(err.response?.data?.error || t('partnerBilling.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleSelect = async (plan) => {
    if (!plan.selfServe) {
      window.location.assign(partnerSalesMailto());
      return;
    }
    const tierType = partnerCheckoutTierType(plan);
    if (!tierType) return;
    try {
      clearError();
      setSelectedTier(tierType);
      const billingInterval = billingIntervalFromYearly(yearly);
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType,
        billingInterval,
        usePartnerAuth: true,
        metadata: { billing_interval: billingInterval }
      });

      if (sessionData?.success && !sessionData.clientSecret && !sessionData.url) {
        if (sessionData.scheduled) {
          const scheduledPlan = PARTNER_PLANS.find((plan) => plan.id === sessionData.partnerPlan);
          const scheduledName = scheduledPlan ? t(planNameKey(scheduledPlan.id)) : sessionData.partnerPlan;
          const effectiveDate = sessionData.effectiveAt ? parseDate(sessionData.effectiveAt) : null;
          setScheduleNotice(t('partnerPlans.scheduledChangeSuccess', {
            plan: scheduledName,
            date: effectiveDate ? format(effectiveDate, 'd MMM yyyy') : t('partnerProfile.noRenewal')
          }));
        }
        await fetchBilling();
        return;
      }

      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('partnerBilling.checkoutFailed'));
    }
  };

  const handleOnboardingPurchase = async () => {
    try {
      clearError();
      setSelectedTier(PARTNER_ONBOARDING_TIER_TYPE);
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType: PARTNER_ONBOARDING_TIER_TYPE,
        usePartnerAuth: true
      });

      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('partnerBilling.checkoutFailed'));
    }
  };

  const handleDeveloperApiPurchase = async () => {
    try {
      clearError();
      setSelectedTier(PARTNER_DEVELOPER_API_TIER_TYPE);
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType: PARTNER_DEVELOPER_API_TIER_TYPE,
        usePartnerAuth: true
      });

      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('partnerBilling.checkoutFailed'));
    }
  };

  const handlePaymentSuccess = async () => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    await fetchBilling();
  };

  const handleCancelScheduledChange = async () => {
    try {
      setCancelScheduleLoading(true);
      setError('');
      const response = await api.delete('/partners/plan/scheduled-change');
      setEntitlements(response.data.entitlements || null);
      setScheduleNotice(t('partnerPlans.cancelScheduledChangeSuccess', {
        plan: response.data.entitlements
          ? t(planNameKey(response.data.entitlements.tier))
          : planName
      }));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerBilling.checkoutFailed'));
    } finally {
      setCancelScheduleLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...partnerPageWrapSx, py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  const periodEnd = parseDate(entitlements?.currentPeriodEnd);
  const currentTier = entitlements?.tier || 'free';
  const currentInterval = entitlements?.billingInterval;
  const selectedInterval = billingIntervalFromYearly(yearly);
  const planName = entitlements ? t(planNameKey(entitlements.tier)) : t('partnerPlans.starter');
  const paymentMethod = stripeAccount?.paymentMethod;
  const scheduledOverLimitSummary = formatOverLimitSummary(entitlements?.scheduledOverLimit, t);
  const overLimitSummary = formatOverLimitSummary(entitlements?.overLimit, t);
  const stripeCancelling = Boolean(
    stripeAccount?.subscription?.cancelAtPeriodEnd
    && !entitlements?.scheduledPlan
  );

  return (
    <Box sx={partnerPageWrapSx}>
      <Box sx={partnerPageInnerSx}>
        <Typography sx={{ pt: { xs: 4.25, md: 4.25 }, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          <Box
            component={RouterLink}
            to="/partner/dashboard"
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: DOCUMENT.ink } }}
          >
            {t('partnerProfile.crumbOverview')}
          </Box>
          {' · '}
          {t('partnerBilling.crumbBilling')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2.5,
            flexWrap: 'wrap',
            mt: 1.75,
            pb: 2,
            borderBottom: `1px solid ${DOCUMENT.ink}`,
          }}
        >
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 30, md: 42 }, fontWeight: 400, lineHeight: 1 }}>
            {t('partnerBilling.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, pb: 0.6 }}>
            {t('partnerBilling.subtitle')}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
        {checkoutError && <Alert severity="error" sx={{ mt: 3 }}>{checkoutError}</Alert>}
        {scheduleNotice && <Alert severity="info" sx={{ mt: 3 }} onClose={() => setScheduleNotice('')}>{scheduleNotice}</Alert>}

        {entitlements && (
          <Section title={t('partnerBilling.currentPlan')}>
            {entitlements.scheduledPlan && (
              <Alert severity="info" sx={{ mt: 1.75, mb: 0 }}>
                {t('partnerPlans.scheduledChangeBody', {
                  currentPlan: planName,
                  nextPlan: entitlements.scheduledPlan.planName,
                  date: parseDate(entitlements.scheduledPlan.effectiveAt)
                    ? format(parseDate(entitlements.scheduledPlan.effectiveAt), 'd MMM yyyy')
                    : t('partnerProfile.noRenewal')
                })}
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancelScheduledChange}
                    disabled={cancelScheduleLoading}
                    sx={partnerOutlineBtnSx}
                  >
                    {cancelScheduleLoading
                      ? t('partnerPlans.cancellingScheduledChange')
                      : t('partnerPlans.cancelScheduledChange')}
                  </Button>
                </Box>
              </Alert>
            )}
            {scheduledOverLimitSummary && entitlements.scheduledPlan && (
              <Alert severity="warning" sx={{ mt: 1.75, mb: 0 }}>
                {t('partnerPlans.scheduledOverLimitWarning', {
                  date: parseDate(entitlements.scheduledPlan.effectiveAt)
                    ? format(parseDate(entitlements.scheduledPlan.effectiveAt), 'd MMM yyyy')
                    : t('partnerProfile.noRenewal'),
                  items: scheduledOverLimitSummary
                })}
              </Alert>
            )}
            {overLimitSummary && (
              <Alert severity="warning" sx={{ mt: 1.75, mb: 0 }}>
                {t('partnerPlans.overLimitWarning', { items: overLimitSummary })}
              </Alert>
            )}
            {stripeCancelling && (
              <Alert severity="info" sx={{ mt: 1.75, mb: 0 }}>
                {t('partnerPlans.stripeCancellingAtPeriodEnd')}
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 2,
                flexWrap: 'wrap',
                py: 2.25,
                borderBottom: `1px solid ${DOCUMENT.rule}`,
              }}
            >
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, lineHeight: 1 }}>
                {planName}
              </Typography>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, textTransform: 'capitalize' }}>
                {t(`partnerBilling.status.${entitlements.status}`, entitlements.status)}
              </Typography>
              {entitlements.foundingPartner && (
                <Typography sx={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                  {t('partnerPlans.founding')}
                </Typography>
              )}
              <Box sx={{ ml: { sm: 'auto' }, display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                <Button onClick={() => setWallOpen(true)} sx={partnerOxbloodBtnSx}>
                  {t('partnerPlans.upgrade')}
                </Button>
                {stripeAccount?.portalAvailable && (
                  <Button onClick={openBillingPortal} disabled={portalLoading} sx={partnerOutlineBtnSx}>
                    {portalLoading ? t('partnerPlans.openingPortal') : t('partnerPlans.managePayment')}
                  </Button>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: '1px',
                bgcolor: DOCUMENT.ruleHard,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                mt: 2.25,
              }}
            >
              <Metric
                label={t('partnerBilling.renewal')}
                value={periodEnd ? format(periodEnd, 'd MMM yyyy') : t('partnerProfile.noRenewal')}
              />
              <Metric
                label={t('partnerPlans.teamSeats', {
                  used: entitlements.usage?.teamMembers ?? 1,
                  limit: formatPlanLimit(
                    entitlements.limits?.includedTeamSeats ?? entitlements.limits?.maxTeamMembers,
                    t('partnerPlans.unlimited')
                  )
                })}
                value={partnerPlanTripsSummary(entitlements, t)}
              />
              <Metric
                label={t('partnerPlans.introductionFeeRate', { rate: entitlements.introductionFeeRate ?? 0 })}
                value={t('partnerPlans.ownClientsFree')}
              />
            </Box>
          </Section>
        )}

        <Section title={t('partnerBilling.stripe')}>
          {stripeAccount?.customerId ? (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: { sm: '46px' },
                m: 0,
              }}
            >
              <Field label={t('partnerBilling.customerId')} value={stripeAccount.customerId} mono />
              <Field label={t('partnerBilling.subscriptionId')} value={stripeAccount.subscriptionId || t('partnerBilling.none')} mono />
              <Field label={t('partnerBilling.stripeEmail')} value={stripeAccount.email || t('partnerBilling.none')} />
              <Field
                label={t('partnerBilling.paymentMethod')}
                value={
                  paymentMethod
                    ? t('partnerBilling.cardOnFile', {
                      brand: paymentMethod.brand,
                      last4: paymentMethod.last4,
                      month: String(paymentMethod.expMonth).padStart(2, '0'),
                      year: paymentMethod.expYear
                    })
                    : t('partnerBilling.noPaymentMethod')
                }
              />
            </Box>
          ) : (
            <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>
              {t('partnerBilling.noStripeAccount')}
            </Typography>
          )}
          {stripeAccount?.portalAvailable && (
            <Box
              component="button"
              onClick={openBillingPortal}
              disabled={portalLoading}
              sx={{ ...partnerLinkSx, mt: 2 }}
            >
              {t('partnerBilling.manageInStripe')}
            </Box>
          )}
        </Section>

        <Section title={t('partnerBilling.plans')}>
          <Typography sx={{ mt: 1.75, mb: 2.5, fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.6 }}>
            {t('partnerBilling.plansBody')}
          </Typography>
          <BillingIntervalToggle
            value={yearly ? 'yearly' : 'monthly'}
            onChange={(next) => setYearly(next === 'yearly')}
            ariaLabel={t('partnerPlans.monthly')}
            monthlyLabel={t('partnerPlans.monthly')}
            yearlyLabel={t('partnerPlans.yearly')}
            yearlySavingLabel={t('partnerPlans.save')}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, alignItems: 'stretch', pt: 1.5 }}>
            {PARTNER_PLANS.map((plan) => {
              const price = yearly ? plan.yearly : plan.monthly;
              const isCurrent = isCurrentSelfServePlan(plan, currentTier, currentInterval, selectedInterval);
              const isScheduled = isScheduledSelfServePlan(plan, entitlements?.scheduledPlan, selectedInterval);
              const isContactSales = !plan.selfServe;
              const canSelect = Boolean(partnerCheckoutTierType(plan) || isContactSales);
              const name = t(planNameKey(plan.id));
              const showRecommendedBadge = plan.recommended && !isCurrent;
              return (
                <Box
                  key={plan.id}
                  sx={{
                    p: 2.5,
                    bgcolor: DOCUMENT.surface,
                    border: `1px solid ${isCurrent || plan.recommended ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                    position: 'relative',
                    overflow: 'visible',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {showRecommendedBadge && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        px: 1.25,
                        py: 0.4,
                        bgcolor: DOCUMENT.oxblood,
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        letterSpacing: '0.04em',
                        borderRadius: '999px',
                        whiteSpace: 'nowrap',
                        zIndex: 1
                      }}
                    >
                      {t('partnerPlans.mostAgents')}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                    {isCurrent
                      ? t('partnerPlans.currentPlan')
                      : plan.recommended
                        ? t(`partnerPlans.${plan.id}Description`)
                        : t(`partnerPlans.${plan.id === 'free' ? 'starter' : plan.id}Description`)}
                  </Typography>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26, fontWeight: 400, mt: 0.75 }}>
                    {name}
                  </Typography>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mt: 0.5, color: isContactSales ? DOCUMENT.accent : DOCUMENT.ink }}>
                    {isContactSales
                      ? t('partnerPlans.callUs')
                      : plan.free
                        ? t('partnerPlans.free')
                        : `$${price}`}
                    {!isContactSales && !plan.free && (
                      <Box component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12, color: DOCUMENT.ink3, ml: 0.75 }}>
                        {yearly ? t('partnerPlans.perYear') : t('partnerPlans.perMonth')}
                      </Box>
                    )}
                  </Typography>
                  <Box sx={{ mt: 1.75, flex: 1 }}>
                    {partnerPlanFeatures(plan, t).map((feature) => (
                      <Typography key={feature} sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 0.6 }}>
                        ✓ {feature}
                      </Typography>
                    ))}
                  </Box>
                  <Box sx={{ minHeight: 28, mt: 1.5, display: 'flex', alignItems: 'flex-end' }}>
                    {plan.trialDays > 0 && !isCurrent && !isContactSales ? (
                      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                        {t('partnerPlans.trialLength', { days: plan.trialDays })}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    fullWidth
                    disabled={isCurrent || isScheduled || checkoutLoading || !canSelect}
                    {...(isContactSales
                      ? { component: 'a', href: partnerSalesMailto() }
                      : { onClick: () => handleSelect(plan) })}
                    sx={{
                      ...(isCurrent ? partnerOutlineBtnSx : partnerOxbloodBtnSx),
                      mt: 1.5,
                      width: '100%',
                    }}
                  >
                    {partnerPlanCtaLabel({
                      plan,
                      currentTier,
                      currentInterval,
                      yearly,
                      t,
                      name,
                      scheduledPlan: entitlements?.scheduledPlan
                    })}
                  </Button>
                </Box>
              );
            })}
            <PartnerDeveloperApiCard
              variant="billing"
              onPurchase={handleDeveloperApiPurchase}
              loading={checkoutLoading}
              included={Boolean(entitlements?.features?.hasProductionApi)}
              canPurchase={Boolean(entitlements?.features?.hasApiSandbox) || entitlements?.tier === 'bespoke'}
            />
            <PartnerOnboardingCard
              variant="billing"
              onPurchase={handleOnboardingPurchase}
              loading={checkoutLoading}
            />
          </Box>
        </Section>

        <Section title={t('partnerBilling.paymentHistory')}>
          {paymentHistory.length === 0 ? (
            <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>
              {t('partnerBilling.noPayments')}
            </Typography>
          ) : (
            <HistoryTable
              rows={paymentHistory}
              t={t}
            />
          )}
        </Section>

        <Section title={t('partnerBilling.commissionInvoices')}>
          {commissionInvoices.length === 0 ? (
            <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>
              {t('partnerBilling.noCommission')}
            </Typography>
          ) : (
            <HistoryTable
              rows={commissionInvoices}
              t={t}
              showPeriod
            />
          )}
        </Section>
        <PartnerWorkspaceFooter />
      </Box>

      <PartnerPlanWall
        open={wallOpen}
        onClose={() => setWallOpen(false)}
        onSuccess={fetchBilling}
        entitlements={entitlements}
        reason="billing"
      />

      {checkoutOpen && checkoutData?.clientSecret && (
        <StripeCheckout
          open={checkoutOpen}
          onClose={() => {
            setCheckoutOpen(false);
            setCheckoutData(null);
            setSelectedTier(null);
          }}
          clientSecret={checkoutData.clientSecret}
          tripTitle={selectedTier === PARTNER_ONBOARDING_TIER_TYPE
            ? t('partnerPlans.onboardingCard.checkoutTitle')
            : selectedTier === PARTNER_DEVELOPER_API_TIER_TYPE
              ? t('partnerPlans.developerApi.checkoutTitle')
              : t('partnerPlans.checkoutTitle')}
          upgradeType={selectedTier}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Box>
  );
};

const Section = ({ title, children }) => (
  <Box sx={{ mt: 5.25 }}>
    <Box sx={{ pb: 1.35, borderBottom: `1px solid ${DOCUMENT.ruleHard}` }}>
      <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const Field = ({ label, value, mono = false }) => (
  <Box sx={{ py: 2, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
    <Typography component="dt" sx={fieldDtSx}>{label}</Typography>
    <Typography
      component="dd"
      sx={{
        lineHeight: 1.5,
        m: 0,
        wordBreak: 'break-all',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        fontSize: mono ? 13 : 15,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const Metric = ({ label, value }) => (
  <Box sx={{ bgcolor: DOCUMENT.surface, px: 2.5, pt: 2.25, pb: 2 }}>
    <Typography sx={fieldDtSx}>{label}</Typography>
    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, lineHeight: 1.15 }}>
      {value}
    </Typography>
  </Box>
);

const HistoryTable = ({ rows, t, showPeriod = false }) => (
  <Box sx={{ mt: 1 }}>
    {rows.map((row) => {
      const date = parseDate(row.date);
      return (
        <Box
          key={`${row.source}-${row.id}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr auto', md: showPeriod ? '140px 1fr 160px 110px auto' : '140px 1fr 110px auto' },
            gap: { xs: '6px 16px', md: '20px' },
            alignItems: 'center',
            py: 1.85,
            borderBottom: `1px solid ${DOCUMENT.rule}`,
          }}
        >
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, fontVariantNumeric: 'tabular-nums' }}>
            {date ? format(date, 'd MMM yyyy') : '—'}
          </Typography>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
            <Typography sx={{ fontSize: 14.5 }}>
              {row.number}
              {row.description ? ` · ${row.description}` : ''}
            </Typography>
            {showPeriod && (row.periodStart || row.periodEnd) && (
              <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.35 }}>
                {[row.periodStart, row.periodEnd].filter(Boolean).join(' – ')}
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 14.5, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(row.amount, row.currency)}
          </Typography>
          <Typography sx={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
            {t(`partnerBilling.invoiceStatus.${row.status}`, row.status)}
          </Typography>
          {row.hostedInvoiceUrl && (
            <Box
              component="a"
              href={row.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={partnerLinkSx}
            >
              {t('partnerBilling.viewInvoice')}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

export default PartnerBilling;
